import { useEffect, useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { urgency, URGENCY_META } from "@/lib/dates";
import StatsBar from "@/components/StatsBar";
import ProductRow from "@/components/ProductRow";
import AddProductDialog from "@/components/AddProductDialog";
import HistoryPanel from "@/components/HistoryPanel";
import ChartsPanel from "@/components/ChartsPanel";
import GoalCard from "@/components/GoalCard";
import ImpactPanel from "@/components/ImpactPanel";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Sprout, Bell, LogOut, Loader2, User, ListFilter } from "lucide-react";

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState(["Autre"]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("manger");
  const [goalKey, setGoalKey] = useState(0);
  const [editing, setEditing] = useState(null);
  const [catFilter, setCatFilter] = useState("all");

  const refresh = useCallback(async () => {
    const [p, s] = await Promise.all([api.get("/products?status=active"), api.get("/stats")]);
    setProducts(p.data);
    setStats(s.data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const cats = await api.get("/categories");
        setCategories(cats.data);
        await refresh();
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  const handleResolve = async (product, status) => {
    setProducts((prev) => prev.filter((x) => x.id !== product.id));
    try {
      await api.patch(`/products/${product.id}/status`, { status });
      const s = await api.get("/stats");
      setStats(s.data);
      setGoalKey((k) => k + 1);
      toast.success(status === "used" ? `« ${product.name} » sauvé !` : `« ${product.name} » jeté.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      refresh();
    }
  };

  const sendReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/notifications/send");
      if (data.status === "empty") toast.info(data.message);
      else toast.success(`Rappel envoyé pour ${data.count} produit(s) à ${user.email}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const activeCategories = Array.from(new Set(products.map((p) => p.category))).sort();
  const filtered = catFilter === "all" ? products : products.filter((p) => p.category === catFilter);
  const groups = { urgent: [], soon: [], later: [] };
  filtered.forEach((p) => groups[urgency(p.days_until)].push(p));
  const order = ["urgent", "soon", "later"];

  return (
    <div className="min-h-screen paper-noise">
      <header className="sticky top-0 z-20 bg-background/85 backdrop-blur-md border-b border-border safe-top">
        <div className="max-w-5xl mx-auto px-4 sm:px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sprout className="h-6 w-6 text-primary" />
            <span className="font-display font-extrabold text-xl sm:text-2xl tracking-tight">FoodFend</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact />
            <Button variant="outline" size="sm" className="rounded-full h-10 min-w-10" onClick={sendReminders}
              disabled={sending} data-testid="send-reminders-btn" aria-label={t("dashboard.emailReminder")}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
              <span className="hidden sm:inline ml-1.5">{t("dashboard.emailReminder")}</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-trigger">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/12 text-primary font-semibold">
                    {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card w-52">
                <DropdownMenuLabel className="font-normal">
                  <p className="font-semibold">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4 mr-2" /> {t("dashboard.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-5 py-6 sm:py-8 pb-24 safe-bottom relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <p className="mono text-xs tracking-mono uppercase text-primary mb-1">{t("dashboard.kicker")}</p>
            <h1 className="font-display text-3xl sm:text-4xl">{t("dashboard.hello", { name: user?.name || "" })} 👋</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">{t("dashboard.subtitle")}</p>
          </div>
          <AddProductDialog categories={categories} onSaved={refresh} />
        </div>

        <div className="mb-8"><StatsBar stats={stats} /></div>

        <div className="mb-8"><GoalCard refreshKey={goalKey} /></div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-muted grid w-full grid-cols-4 sm:inline-flex sm:w-auto" data-testid="dashboard-tabs">
            <TabsTrigger value="manger" data-testid="tab-manger" className="text-xs sm:text-sm px-2 sm:px-3">{t("dashboard.tabEat")}</TabsTrigger>
            <TabsTrigger value="historique" data-testid="tab-historique" className="text-xs sm:text-sm px-2 sm:px-3">{t("dashboard.tabHistory")}</TabsTrigger>
            <TabsTrigger value="graphiques" data-testid="tab-graphiques" className="text-xs sm:text-sm px-2 sm:px-3">{t("dashboard.tabCharts")}</TabsTrigger>
            <TabsTrigger value="impact" data-testid="tab-impact" className="text-xs sm:text-sm px-2 sm:px-3">{t("dashboard.tabImpact")}</TabsTrigger>
          </TabsList>

          <TabsContent value="manger" className="mt-6">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            ) : products.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-border rounded-2xl" data-testid="active-empty">
                <Sprout className="h-8 w-8 mx-auto text-primary mb-3" />
                <p className="font-display text-xl">{t("dashboard.emptyTitle")}</p>
                <p className="text-sm text-muted-foreground mt-1">{t("dashboard.emptySub")}</p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-5">
                  <ListFilter className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Select value={catFilter} onValueChange={setCatFilter}>
                    <SelectTrigger className="h-10 w-full sm:w-[220px] rounded-full" data-testid="category-filter-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card">
                      <SelectItem value="all">{t("dashboard.allCategories")}</SelectItem>
                      {activeCategories.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {filtered.length === 0 ? (
                  <div className="text-center py-16 border border-dashed border-border rounded-2xl" data-testid="filter-empty">
                    <p className="text-muted-foreground">{t("dashboard.emptyCategory")}</p>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {order.filter((k) => groups[k].length).map((k) => (
                      <section key={k} data-testid={`group-${k}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className={`h-2 w-2 rounded-full ${URGENCY_META[k].dot}`} />
                          <span className="mono text-xs uppercase tracking-mono text-muted-foreground">
                            {URGENCY_META[k].label} ({groups[k].length})
                          </span>
                        </div>
                        <div className="space-y-2.5">
                          <AnimatePresence initial={false}>
                            {groups[k].map((p) => (
                              <ProductRow key={p.id} product={p} onResolve={handleResolve} onEdit={setEditing} />
                            ))}
                          </AnimatePresence>
                        </div>
                      </section>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="historique" className="mt-6">
            {tab === "historique" && <HistoryPanel />}
          </TabsContent>
          <TabsContent value="graphiques" className="mt-6">
            {tab === "graphiques" && <ChartsPanel />}
          </TabsContent>
          <TabsContent value="impact" className="mt-6">
            {tab === "impact" && <ImpactPanel />}
          </TabsContent>
        </Tabs>

        <footer className="mt-16 pt-6 border-t border-dashed border-border text-center text-xs text-muted-foreground">
          <User className="h-3 w-3 inline mr-1" /> {t("dashboard.footer")}
        </footer>
      </main>

      {editing && (
        <AddProductDialog
          categories={categories}
          product={editing}
          open={!!editing}
          onOpenChange={(o) => { if (!o) setEditing(null); }}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}

      {/* Mobile FAB — quick add product */}
      <button
        type="button"
        onClick={() => document.querySelector('[data-testid="open-add-product-btn"]')?.click()}
        data-testid="mobile-add-fab"
        aria-label={t("dashboard.add")}
        className="sm:hidden fixed bottom-5 right-5 z-30 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-[0_10px_30px_-6px_hsl(152_76%_40%/0.6)] flex items-center justify-center active:scale-95 transition-transform"
        style={{ marginBottom: "env(safe-area-inset-bottom)" }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><path d="M12 5v14M5 12h14"/></svg>
      </button>
    </div>
  );
}
